/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// SAKETE: real tax variants and recorded PV qualification on Focal and PageFly.
(function () {
  'use strict';
  var initialized = new WeakSet();
  var pageflySequence = 0;
  var scanning = false;

  function controllerData(controller) {
    if (controller._saketeVariants) return controller._saketeVariants;
    try {
      controller._saketeVariants = JSON.parse(controller.querySelector('[data-sakete-vat-variants]').textContent).map(function (entry) {
        return Object.assign({}, entry.variant, { solarOnly: entry.solarOnly === true });
      });
    } catch (error) { controller._saketeVariants = []; }
    return controller._saketeVariants;
  }

  function selectedVariant(controller) {
    var selected = Array.from(controller.querySelectorAll('[data-sakete-option-position]')).map(function (selector) { return selector.value; });
    return controllerData(controller).find(function (variant) {
      return variant.options.length === selected.length && variant.options.every(function (value, index) { return value === selected[index]; });
    }) || null;
  }

  function money(controller, value) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: controller.dataset.currency || 'EUR' }).format(value / 100);
  }

  function validateProject(controller) {
    var kwp = controller.querySelector('[data-sakete-project-kwp]');
    var building = controller.querySelector('[data-sakete-project-building]');
    if (!kwp || !building) return;
    kwp.setCustomValidity(!kwp.disabled && kwp.value && !/^\d+(?:\.\d+)?$/.test(kwp.value) ? 'Bitte geben Sie eine positive Leistung als Zahl ein.' : '');
    var blocked = !kwp.disabled && building.value === 'Anderer Standort (Bruttoleistung höchstens 30 kWp)' && Number(kwp.value) > 30;
    building.setCustomValidity(blocked ? 'Bei diesem Standort und mehr als 30 kWp wählen Sie bitte 19 % Umsatzsteuer.' : '');
  }

  function setZeroState(controller, variant) {
    var zero = Boolean(variant && variant.taxable === false);
    var declaration = controller.querySelector('[data-sakete-vat-zero]');
    var previousZero = controller.dataset.zeroActive;
    if (previousZero && previousZero !== String(zero)) {
      declaration.querySelectorAll('input[type="checkbox"]').forEach(function (checkbox) { checkbox.checked = false; });
    }
    controller.dataset.zeroActive = String(zero);
    declaration.hidden = !zero;
    declaration.disabled = !zero;
    declaration.querySelectorAll('[data-sakete-zero-required]').forEach(function (input) {
      input.required = zero;
      input.disabled = !zero;
      if (!zero) input.setCustomValidity('');
    });
    var solar = declaration.querySelector('[data-sakete-solar-required]');
    var solarActive = Boolean(zero && variant.solarOnly);
    declaration.querySelector('[data-sakete-solar-only]').hidden = !solarActive;
    if (!solarActive) solar.checked = false;
    solar.required = solarActive;
    solar.disabled = !solarActive;
    if (!solarActive) solar.setCustomValidity('');
    var note = controller.querySelector('[data-sakete-vat-standard-note]');
    if (note) note.hidden = zero || !variant;
    var priceNote = controller.querySelector('[data-sakete-vat-price-note]');
    if (priceNote) priceNote.textContent = !variant ? '' : (zero ? 'inkl. 0 % MwSt. bei erfüllten Voraussetzungen' : 'inkl. 19 % MwSt.');
    var error = controller.querySelector('[data-sakete-vat-zero-error]');
    if (error && !zero) error.hidden = true;
    validateProject(controller);
  }

  function buttonsFor(form) {
    return Array.from(form.querySelectorAll('[data-product-add-to-cart-button], [data-pf-type^="ProductATC"], button[name="add"]'));
  }

  function syncVariant(controller, notify) {
    var form = controller.closest('form');
    if (!form) return null;
    var variant = selectedVariant(controller);
    var previousId = controller.dataset.selectedVariant;
    var input = form.querySelector('input[name="id"]');
    if (input) { input.value = variant ? String(variant.id) : ''; input.disabled = !variant; }
    controller.dataset.selectedVariant = variant ? String(variant.id) : '';
    setZeroState(controller, variant);
    var unavailable = controller.querySelector('[data-sakete-variant-unavailable]');
    if (unavailable) unavailable.hidden = Boolean(variant && variant.available);
    buttonsFor(form).forEach(function (button) {
      button.disabled = !variant || !variant.available || form.dataset.saketeAdding === 'true';
      button.setAttribute('aria-disabled', String(button.disabled));
      var text = button.querySelector('.loader-button__text');
      if (text) text.textContent = variant && variant.available ? (button.getAttribute('data-button-content') || 'In den Warenkorb') : 'Ausverkauft';
      else if (form.dataset.saketePageflyControlled === 'true') button.textContent = variant && variant.available ? 'In den Warenkorb' : 'Ausverkauft';
    });
    var price = controller.querySelector('[data-sakete-vat-price]');
    if (price) price.textContent = variant ? money(controller, variant.price) : 'Auswahl nicht verfügbar';
    var productBox = form.closest('[data-pf-type="ProductBox"]');
    if (productBox && variant) {
      productBox.querySelectorAll('[data-product-type="price"][data-product-price="true"], [data-pf-type="ProductPrice2Item"]').forEach(function (element) { element.textContent = money(controller, variant.price); });
    } else if (variant) {
      var info = form.closest('product-rerender') || form.closest('.product__info');
      if (info) {
        info.querySelectorAll('.product-meta .price-list .price:not(.price--compare), .product-meta__price-list-container .price:not(.price--compare), .product-info__price .price:not(.price--compare)').forEach(function (element) { element.textContent = money(controller, variant.price); });
        info.querySelectorAll('.price--compare').forEach(function (element) { element.hidden = true; });
      }
    }
    if (notify && variant && previousId !== String(variant.id)) {
      var url = new URL(window.location.href);
      if (url.pathname.indexOf('/products/') !== -1) {
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url.toString());
      }
      form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant, formId: form.getAttribute('id') } }));
      form.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: variant, formId: form.getAttribute('id'), saketeControlled: true } }));
    }
    return variant;
  }

  function validateController(controller) {
    var form = controller.closest('form');
    var variant = syncVariant(controller, false);
    if (!variant || !variant.available) return false;
    controller.querySelectorAll('[data-sakete-vat-zero] input[type="text"]').forEach(function (input) {
      input.setCustomValidity(!input.disabled && input.required && !input.value.trim() ? 'Bitte füllen Sie dieses Feld aus.' : '');
    });
    validateProject(controller);
    var valid = form.checkValidity();
    var error = controller.querySelector('[data-sakete-vat-zero-error]');
    if (error) error.hidden = valid || variant.taxable !== false;
    if (!valid) form.reportValidity();
    return valid;
  }

  function initializeController(controller) {
    if (initialized.has(controller)) return;
    var form = controller.closest('form');
    if (!form) return;
    initialized.add(controller);
    form.dataset.saketeVatControlled = 'true';
    var focalInfo = form.closest('product-rerender');
    if (focalInfo) focalInfo.querySelectorAll('.globo-swatch-product-detail').forEach(function (element) { element.hidden = true; element.style.setProperty('display', 'none', 'important'); });
    var input = form.querySelector('input[name="id"]');
    var variant = controllerData(controller).find(function (entry) { return input && String(entry.id) === input.value; });
    if (variant) controller.querySelectorAll('[data-sakete-option-position]').forEach(function (selector) { selector.value = variant.options[Number(selector.dataset.saketeOptionPosition)]; });
    syncVariant(controller, false);
  }

  function hideOldPageflyControls(form) {
    form.querySelectorAll('[data-pf-type="ProductVariantSwatches"], .globo-swatch-product-detail, .shopify-payment-button, shopify-accelerated-checkout').forEach(function (element) {
      element.hidden = true;
      element.style.setProperty('display', 'none', 'important');
    });
  }

  function injectPagefly() {
    var template = document.getElementById('sakete-pagefly-vat-template');
    if (!(template instanceof HTMLTemplateElement)) return;
    document.querySelectorAll('.pf-product-form[data-productid="' + template.dataset.productId + '"]').forEach(function (form) {
      var atc = form.querySelector('[data-pf-type^="ProductATC"]');
      if (!atc) return;
      if (!form.querySelector('[data-sakete-vat-controller]')) {
        var fragment = template.content.cloneNode(true);
        var suffix = '-copy-' + (++pageflySequence);
        fragment.querySelectorAll('[id]').forEach(function (element) { element.id += suffix; });
        fragment.querySelectorAll('label[for]').forEach(function (label) { label.htmlFor += suffix; });
        atc.before(fragment);
      }
      form.dataset.saketePageflyControlled = 'true';
      hideOldPageflyControls(form);
    });
  }

  function scan() {
    scanning = false;
    injectPagefly();
    document.querySelectorAll('form [data-sakete-vat-controller]').forEach(initializeController);
  }

  function queueScan() {
    if (!scanning) { scanning = true; requestAnimationFrame(scan); }
  }

  function notifyPagefly(controller, text, isError) {
    var notice = controller.querySelector('[data-sakete-pf-notification]');
    if (!notice) return;
    notice.textContent = text;
    notice.hidden = false;
    notice.setAttribute('role', isError ? 'alert' : 'status');
  }

  async function addPagefly(form) {
    if (form.dataset.saketeAdding === 'true') return;
    var controller = form.querySelector('[data-sakete-vat-controller]');
    if (!controller || !validateController(controller)) return;
    form.dataset.saketeAdding = 'true';
    buttonsFor(form).forEach(function (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    try {
      var data = new FormData(form);
      data.set('id', controller.dataset.selectedVariant);
      data.set('sections', 'mini-cart');
      data.delete('option1'); data.delete('option2'); data.delete('option3');
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      var variables = window.themeVariables || { routes: {}, settings: {} };
      var response = await fetch((variables.routes.cartAddUrl || root + 'cart/add') + '.js', { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      var added = await response.json();
      if (!response.ok) throw new Error(added.description || 'Das Produkt konnte nicht hinzugefügt werden.');
      form.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: added.items ? added.items[0] : added } }));
      notifyPagefly(controller, 'Das Produkt wurde dem Warenkorb hinzugefügt.', false);
      if (variables.settings.cartType === 'page' || variables.settings.pageType === 'cart') {
        window.location.assign(root + 'cart');
        return;
      }
      var cartResponse = await fetch((variables.routes.cartUrl || root + 'cart') + '.js');
      if (!cartResponse.ok) throw new Error('Im Warenkorb hinzugefügt. Bitte öffnen Sie den Warenkorb.');
      var cart = await cartResponse.json();
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: variables.settings.cartType === 'drawer' } }));
      form.dispatchEvent(new CustomEvent('cart-notification:show', { bubbles: true, detail: { status: 'success', error: '' } }));
    } catch (error) {
      notifyPagefly(controller, error.message || 'Bitte versuchen Sie es erneut.', true);
    } finally {
      delete form.dataset.saketeAdding;
      buttonsFor(form).forEach(function (button) { button.removeAttribute('aria-busy'); });
      syncVariant(controller, false);
    }
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var controller = target.closest('[data-sakete-vat-controller]');
    if (!controller) return;
    if (target.matches('[data-sakete-option-position]')) syncVariant(controller, true);
    else validateProject(controller);
  });
  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    var controller = target.closest('[data-sakete-vat-controller]');
    if (!controller) return;
    if (target.type === 'text') target.setCustomValidity(target.required && !target.disabled && !target.value.trim() ? 'Bitte füllen Sie dieses Feld aus.' : '');
    validateProject(controller);
  });
  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var button = event.target.closest('[data-pf-type^="ProductATC"], button[name="add"]');
    var form = button && button.closest('form[data-sakete-pagefly-controlled="true"]');
    if (!form) return;
    event.preventDefault(); event.stopImmediatePropagation();
    addPagefly(form);
  }, true);
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-sakete-vat-cart-blocked="true"]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      var drawer = form.closest('cart-drawer');
      var message = form.querySelector('[data-sakete-vat-cart-message]') || (drawer && drawer.querySelector('[data-sakete-vat-cart-message]'));
      if (message) message.focus();
      return;
    }
    if (form.dataset.saketePageflyControlled === 'true') {
      event.preventDefault(); event.stopImmediatePropagation();
      addPagefly(form);
      return;
    }
    var controller = form.querySelector('[data-sakete-vat-controller]');
    if (controller && !validateController(controller)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('variant:changed', function (event) {
    if (event.detail && event.detail.saketeControlled) return;
    var form = event.target instanceof HTMLFormElement ? event.target : null;
    var controller = form && form.querySelector('[data-sakete-vat-controller]');
    if (controller) syncVariant(controller, false);
  });
  document.addEventListener('PageFlyHelperContentLoaded', queueScan);
  document.addEventListener('shopify:section:load', queueScan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true }); else scan();
  new MutationObserver(function (records) {
    if (records.some(function (record) { return Array.from(record.addedNodes).some(function (node) { return node.nodeType === 1; }); })) queueScan();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
